/**
 * Manual Review Queue — dashboard summary widget (Phase H).
 *
 * Cheap counters wired to /api/manual-review/counts. Rendered on the
 * dashboard for anyone with review privileges (Admin / Supervisor /
 * BillingLead / Integration / AZAI staff). Values are counts only —
 * no PHI ever surfaces here.
 *
 * Refresh cadence: on-mount + every 60 seconds. Manual poll suffices
 * for a dashboard glance; the full queue page uses tighter refresh.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Warning,
  Clock,
  XCircle,
  Bell,
  ArrowRight,
} from '@phosphor-icons/react';

const REFRESH_MS = 60_000;

const REVIEW_ROLES = new Set(['Admin', 'Supervisor', 'BillingLead', 'Integration']);

const isReviewCapable = (user) =>
  user?.entity_type === 'azai' || REVIEW_ROLES.has(user?.role);

const CounterTile = ({ icon: Icon, label, value, tone, dataTestid }) => {
  const toneClasses = {
    critical: 'bg-red-50 border-red-200 text-red-900',
    high: 'bg-amber-50 border-amber-200 text-amber-900',
    medium: 'bg-blue-50 border-blue-200 text-blue-900',
    zero: 'bg-slate-50 border-slate-200 text-slate-600',
  };
  const activeTone = value > 0 ? tone : 'zero';
  return (
    <div
      className={`rounded-lg border p-3 ${toneClasses[activeTone]}`}
      data-testid={dataTestid}
    >
      <div className="flex items-center gap-2">
        <Icon size={16} weight={value > 0 ? 'fill' : 'regular'} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
};

export const ManualReviewWidget = ({ currentUser }) => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await api.get('/manual-review/counts');
      setCounts(data);
      setError(null);
    } catch (e) {
      // Safe user-facing error — never surface backend detail bodies.
      setError('Unable to load review queue counts.');
      console.debug('[manual-review-widget]', e?.response?.status || 'network');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isReviewCapable(currentUser)) return undefined;
    fetchCounts();
    const timer = setInterval(fetchCounts, REFRESH_MS);
    return () => clearInterval(timer);
  }, [currentUser, fetchCounts]);

  if (!isReviewCapable(currentUser)) return null;

  const total = counts?.total ?? 0;

  return (
    <div
      className="border border-slate-200 rounded-xl bg-white p-4 space-y-3"
      data-testid="manual-review-widget"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Warning size={18} className="text-amber-600" weight="fill" />
            Manual Review Queue
            {total > 0 && (
              <Badge className="bg-red-100 text-red-800 hover:bg-red-100" data-testid="mrq-total-badge">
                {total}
              </Badge>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Fired watchdogs, unmatched acks, and stalled submissions requiring operator attention.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/admin/manual-review')}
          data-testid="mrq-open-btn"
        >
          Open queue
          <ArrowRight size={14} className="ml-1.5" />
        </Button>
      </div>

      {loading && (
        <p className="text-xs text-slate-400" data-testid="mrq-loading">Loading counters…</p>
      )}

      {error && (
        <p className="text-xs text-red-600" data-testid="mrq-error">{error}</p>
      )}

      {counts && !loading && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="mrq-counters">
          <CounterTile
            icon={Clock}
            label="Watchdogs fired"
            value={counts.watchdogs_fired || 0}
            tone="high"
            dataTestid="mrq-count-watchdogs"
          />
          <CounterTile
            icon={XCircle}
            label="Unmatched acks"
            value={counts.acks_unmatched || 0}
            tone="high"
            dataTestid="mrq-count-acks"
          />
          <CounterTile
            icon={XCircle}
            label="Rejected"
            value={counts.submissions_rejected || 0}
            tone="critical"
            dataTestid="mrq-count-rejected"
          />
          <CounterTile
            icon={Warning}
            label="Manual review"
            value={counts.submissions_manual_review || 0}
            tone="high"
            dataTestid="mrq-count-manual"
          />
          <CounterTile
            icon={Bell}
            label="Open alerts"
            value={counts.alerts_open || 0}
            tone="medium"
            dataTestid="mrq-count-alerts"
          />
        </div>
      )}
    </div>
  );
};

export default ManualReviewWidget;
